<template>
  <v-text-field
    :id="id ? id : undefined"
    :readonly="readonly"
    :label="label"
    :maxlength="maxlength"
    :rules="[...rules, ...additionalRules]"
    :data-tab-key="dataTabKey"
    v-model.lazy="value"
    v-money="formatMoney"
    @change="emitChanges"
    @keydown="verifySignal"
    @keypress="blockByValue"
    :autofocus="autofocus"
    ref="textField"
    :outlined="outlined"
    :dense="dense"
    :hint="hint"
    :persistent-hint="persistentHint"
    :validate-on-blur="validateOnBlur"
    :disabled="disabled"
    :hide-details="hideDetails"
  />
</template>

<script>
// Utils
import numberUtils from '../../../../common/utils/number.js';

export default {
  model: {
    prop: "inputValue",
    event: "change",
  },
  props: {
    autofocus: { type: Boolean, default: false },
    inputValue: {
      type: [Number,String],
      default: 0,
    },
    label: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      default: '',
    },
    allowNegative: {
      type: Boolean,
      required: false,
      default: false,
    },
    rules: {
      type: Array,
      required: false,
      default: () => [],
    },
    maxValue: {
      type: Number,
      default: null,
    },
    maxlength: {
      type: Number,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: true,
    },
    prefix: {
      type: String,
      default: "R$ ",
    },
    suffix: { 
      type: String,
      required: false,
      default: ""
    },
    hint: { 
      type: String,
      required: false,
      default: ""
    },
    persistentHint: {
      type: Boolean,
      required: false,
      default: false
    },
    min: {
      type: [Number, String],
      default: null,
    },
    max: {
      type: [Number, String],
      default: null,
    },
    precision: {
      type: [Number, String],
      required: false,
      default: 2,
    },
    validateOnBlur: {
      type: Boolean,
      default: false,
    },
    truncate: {
      type: Boolean,
      default: false,
    },
    dataTabKey: {
      type: String,
      default: "",
    },
    hideDetails: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    value: 0,
    additionalRules: [],
  }),

  watch: {
    inputValue(newValue) {
      this.setNewValueManually(newValue);
    },

    autofocus(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.textField.focus();
          this.onFocus();
        });
      }
    },
  },

  mounted() {
    this.setNewValueManually(this.inputValue);
    this.setAdditionalRules();
    // if not present, the component initializes with validation errors
    this.$refs.textField.resetValidation();
  },

  beforeUpdate(){
    this.setAdditionalRules();
  },

  computed: {
    formatMoney() {
      return {
        decimal: ",",
        thousands: ".",
        prefix: this.prefix,
        precision: this.precision,
        suffix: this.suffix
      };
    },
  },

  methods: {
    onFocus() {
      this.$emit("onFocus");
    },

    // Hack to force mask to render and dont remove zero and integer values.
    // When the mask receives the numbers zero to the right of the comma, the component broke
    setNewValueManually(value) {
      let parsedValue = 0;
      const safeValue = value == null ? 0 : value;

      if (this.truncate) {
        parsedValue = numberUtils.truncateDecimalValue(parseFloat(safeValue), this.precision).toFixed(this.precision);
      } else {
        parsedValue = parseFloat(safeValue).toFixed(this.precision);
      }

      this.value = safeValue;

      this.$refs.textField.$el.getElementsByTagName(
        "input"
      )[0].value = parsedValue;
    },

    // This method will be call only when the v-model change
    // Emit changes for each change in value(watch) generates errors if you pass numbers
    // Ending with 0 (Example: 100 is show 0,01)

    emitChanges() {
      this.$emit("change", parseFloat(this.removeMask(this.value)));
    },

    // Dont allow negative values
    verifySignal(e) {
      if (!this.allowNegative && e.key && e.key === "-") e.preventDefault();
    },

    // TODO: It's not good. Create better validation
    blockByValue(e) {
      if (!this.maxValue) return;

      const inputValue = e.key;
      const value =
        this.maxValue - (this.removeMask(e.target.value) + Number(inputValue));

      if (value <= 0) {
        e.preventDefault();
      }
    },

    removeMask(value) {
      if (typeof value === "string") {
        return parseFloat(
          value
            .replace(this.prefix, "")
            .replace(/\./g, "")
            .replace(",", ".")
            .replace(this.suffix, "")
        );
      } else if (typeof value === "number") {
        return value;
      }

      return 0;
    },

    setAdditionalRules() {
      this.additionalRules = [];

      const min = parseFloat(this.min);

      if (min) {
        this.additionalRules.push(
          (value) => {
            const valueNoMask = this.removeMask(value).toFixed(this.precision);
            const minRounded = min.toFixed(this.precision);

            return +(valueNoMask) >= +(minRounded) || `O valor mínimo permitido é ${this.prefix + this.formatNumber(minRounded) + this.suffix}`;
          }
        );
      }

      const max = parseFloat(this.max);

      if (max || max == 0) {
        this.additionalRules.push(
          (value) => {
            const valueNoMask = this.removeMask(value).toFixed(this.precision);
            const maxRounded = max.toFixed(this.precision);

            return +(valueNoMask) <= +(maxRounded) || `O valor máximo permitido é ${this.prefix + this.formatNumber(maxRounded) + this.suffix}`;
          }
        );
      }
    },

    formatNumber(value) {
      return numberUtils.formatNumber(value, this.precision);
    }
  },
};
</script>
